import { useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { ReportModal } from '@/pages/GamAdvertisers/components/ReportModal';
import type { NavUtm } from '@/types/navigation-report';
import { fmtNav } from './format';

interface UtmsTableProps {
    items: NavUtm[];
}

const PREVIEW = 10;
const TD = 'px-2 py-2 text-right tabular-nums text-foreground/90';
const utmKey = (u: NavUtm) => `${u.utm_source}|${u.utm_medium}|${u.utm_campaign}`;

function Rows({ items }: UtmsTableProps) {
    return (
        <table className="w-full min-w-[640px] text-xs">
            <thead>
                <tr className="border-b border-border text-[10px] uppercase tracking-[0.1em] text-muted-foreground">
                    <th className="px-2 py-2 text-left">Source</th>
                    <th className="px-2 py-2 text-left">Medium</th>
                    <th className="px-2 py-2 text-left">Campaign</th>
                    <th className="px-2 py-2 text-right">Sessões</th>
                    <th className="px-2 py-2 text-right">% da P1</th>
                </tr>
            </thead>
            <tbody>
                {items.map((row) => (
                    <tr key={utmKey(row)} className="border-b border-border/60">
                        <td className="max-w-[220px] truncate px-2 py-2 text-left font-medium text-foreground" title={fmtNav.utm(row.utm_source)}>{fmtNav.utm(row.utm_source)}</td>
                        <td className="max-w-[180px] truncate px-2 py-2 text-left text-foreground/90" title={fmtNav.utm(row.utm_medium)}>{fmtNav.utm(row.utm_medium)}</td>
                        <td className="max-w-[220px] truncate px-2 py-2 text-left text-foreground/90" title={fmtNav.utm(row.utm_campaign)}>{fmtNav.utm(row.utm_campaign)}</td>
                        <td className={`${TD} font-medium text-foreground`}>{fmtNav.num(row.sessions)}</td>
                        <td className={TD}>{fmtNav.pct1(row.share_pct)}</td>
                    </tr>
                ))}
                {items.length === 0 && (
                    <tr><td colSpan={5} className="px-2 py-6 text-center text-muted-foreground">Sem UTMs no período</td></tr>
                )}
            </tbody>
        </table>
    );
}

/** UTMs com que as sessões ENTRARAM nesta P1: resumo na tela, completo em modal com busca. */
export function UtmsTable({ items }: UtmsTableProps) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');
    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        return q ? items.filter((u) => utmKey(u).toLowerCase().includes(q)) : items;
    }, [items, search]);

    return (
        <div className="rounded-lg border border-border bg-card">
            <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
                    UTMs de entrada <span className="text-foreground/30">· {fmtNav.num(items.length)} combinação(ões)</span>
                </p>
                {items.length > PREVIEW && (
                    <button type="button" onClick={() => setOpen(true)} className="rounded-md border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:bg-accent hover:text-foreground">
                        Ver todas ({fmtNav.num(items.length)})
                    </button>
                )}
            </div>
            <div className="overflow-x-auto font-mono">
                <Rows items={items.slice(0, PREVIEW)} />
            </div>

            <ReportModal open={open} onClose={() => setOpen(false)} title="UTMs de entrada" subtitle="source / medium / campaign do primeiro hit da sessão">
                <label className="mb-3 flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Search className="h-3.5 w-3.5" />
                    <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Buscar source, medium ou campaign…" className="h-8 w-72 rounded-md border border-border bg-background px-2 text-xs text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring" />
                </label>
                <div className="overflow-x-auto font-mono">
                    <Rows items={filtered} />
                </div>
            </ReportModal>
        </div>
    );
}
